import type { RendererProps } from "../types";

import { MediaPreviewCarousel } from "./MediaPreviewCarousel";

import type {
  JsonContentItem,
  MultiContentItem,
  TextContentItem,
} from "@/routes/smart-table/utils/multiContent";

function MultiContentTextBlock({ item }: { item: TextContentItem }) {
  if (item.text.trim().length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
      <p className="whitespace-pre-wrap break-words text-[14px] leading-relaxed text-foreground/90">
        {item.text}
      </p>
    </div>
  );
}

function MultiContentJsonBlock({ item }: { item: JsonContentItem }) {
  let jsonText = "";
  try {
    jsonText = JSON.stringify(item.value, null, 2);
  } catch {
    jsonText = String(item.value);
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border/40 bg-muted/10 p-4">
      <pre className="m-0 font-mono text-xs leading-relaxed text-foreground/80">{jsonText}</pre>
    </div>
  );
}

function MultiContentBlock({ item }: { item: MultiContentItem }) {
  if (item.type === "text") {
    return <MultiContentTextBlock item={item} />;
  }

  if (item.type === "json") {
    return <MultiContentJsonBlock item={item} />;
  }

  return null;
}

export function MultiContentRenderer({ content }: RendererProps) {
  const payload = content.multiContentPayload;

  if (!payload || payload.items.length === 0) {
    return null;
  }

  const mediaItems = payload.items.filter(
    (item): item is Extract<MultiContentItem, { type: "output_media" }> =>
      item.type === "output_media",
  );

  return (
    <div className="flex w-full flex-col gap-3">
      {payload.items.map((item, index) =>
        item.type === "output_media" ? null : (
          // eslint-disable-next-line react/no-array-index-key
          <MultiContentBlock key={index} item={item} />
        ),
      )}
      {mediaItems.length > 0 ? <MediaPreviewCarousel items={mediaItems} /> : null}
    </div>
  );
}
